import React, { useState } from 'react';
import { 
  DocumentTextIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

const Strategies: React.FC = () => {
  const [strategies] = useState([
    {
      id: 1,
      name: '그리드 트레이딩',
      description: '일정 간격으로 매수/매도 주문을 배치하는 전략',
      status: 'active',
      profit: '+12.5%',
      trades: 156,
      winRate: '68%',
      params: {
        gridSize: 10,
        gridGap: '0.5%',
        investment: '$1,000'
      }
    },
    {
      id: 2,
      name: 'RSI 역추세',
      description: 'RSI 지표를 활용한 과매수/과매도 거래',
      status: 'paused',
      profit: '+8.3%',
      trades: 89,
      winRate: '62%',
      params: {
        rsiPeriod: 14,
        oversold: 30,
        overbought: 70
      }
    },
    {
      id: 3,
      name: 'MACD 크로스',
      description: 'MACD 골든/데드 크로스 시그널 활용',
      status: 'testing',
      profit: '+5.2%',
      trades: 45,
      winRate: '58%',
      params: {
        fast: 12,
        slow: 26,
        signal: 9
      }
    },
    {
      id: 4,
      name: 'AI 예측 모델',
      description: '머신러닝 기반 가격 예측 전략',
      status: 'inactive',
      profit: '-2.1%',
      trades: 23,
      winRate: '45%',
      params: {
        model: 'LSTM',
        timeframe: '1H',
        features: 15
      }
    }
  ]);

  const [selectedStrategy, setSelectedStrategy] = useState<any>(null);



  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-500" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">전략 관리</h2>
        </div>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          새 전략 추가
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 전략 목록 */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">활성 전략</h3>
          {strategies.map(strategy => (
            <div
              key={strategy.id}
              className={`bg-white dark:bg-dark-700 border rounded-lg p-4 cursor-pointer transition-all ${
                selectedStrategy?.id === strategy.id
                  ? 'border-blue-500 ring-2 ring-blue-500/20'
                  : 'border-light-300 dark:border-dark-600 hover:border-blue-400 dark:hover:border-blue-500'
              }`}
              onClick={() => setSelectedStrategy(strategy)}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900 dark:text-white">{strategy.name}</h3>
                <span className={`px-2 py-1 text-xs rounded ${
                  strategy.status === 'active' 
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {strategy.status === 'active' ? '활성' : '비활성'}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{strategy.description}</p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">수익률: <span className="font-medium text-green-600 dark:text-green-400">{strategy.profit}</span></span>
                <span className="text-gray-600 dark:text-gray-400">거래 횟수: <span className="font-medium text-gray-900 dark:text-white">{strategy.trades}</span></span>
              </div>
            </div>
          ))}
        </div>

        {/* 전략 상세 정보 */}
        <div>
          {selectedStrategy ? (
            <div className="bg-white dark:bg-dark-700 border border-light-300 dark:border-dark-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">전략 상세 설정</h3>
              
              {(() => {
                const strategy = strategies.find(s => s.id === selectedStrategy.id);
                if (!strategy) return null;

                return (
                  <>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="text-sm text-gray-600 dark:text-gray-400">전략 이름</label>
                        <input
                          type="text"
                          value={strategy.name}
                          className="w-full mt-1 bg-white dark:bg-dark-600 border border-light-300 dark:border-dark-500 text-gray-900 dark:text-white rounded px-3 py-2"
                          readOnly
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-gray-600 dark:text-gray-400">설명</label>
                        <textarea
                          value={strategy.description}
                          className="w-full mt-1 bg-white dark:bg-dark-600 border border-light-300 dark:border-dark-500 text-gray-900 dark:text-white rounded px-3 py-2 h-20"
                          readOnly
                        />
                      </div>
                    </div>

                    <h4 className="text-gray-900 dark:text-white font-medium mb-3">매개변수</h4>
                    <div className="space-y-3">
                      {Object.entries(strategy.params).map(([key, value]) => (
                        <div key={key}>
                          <label className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                          <input
                            type="text"
                            value={value}
                            className="w-full mt-1 bg-white dark:bg-dark-600 border border-light-300 dark:border-dark-500 text-gray-900 dark:text-white rounded px-3 py-2"
                            readOnly
                          />
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-6 border-t border-light-300 dark:border-dark-500">
                      <h4 className="text-gray-900 dark:text-white font-medium mb-3">성과 지표</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-light-100 dark:bg-dark-600 rounded p-3">
                          <p className="text-sm text-gray-600 dark:text-gray-400">총 수익</p>
                          <p className={`text-lg font-bold ${strategy.profit.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>
                            {strategy.profit}
                          </p>
                        </div>
                        <div className="bg-light-100 dark:bg-dark-600 rounded p-3">
                          <p className="text-sm text-gray-600 dark:text-gray-400">승률</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">{strategy.winRate}</p>
                        </div>
                        <div className="bg-light-100 dark:bg-dark-600 rounded p-3">
                          <p className="text-sm text-gray-600 dark:text-gray-400">거래 횟수</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">{strategy.trades}</p>
                        </div>
                        <div className="bg-light-100 dark:bg-dark-600 rounded p-3">
                          <p className="text-sm text-gray-600 dark:text-gray-400">평균 수익</p>
                          <p className="text-lg font-bold text-green-500">+0.08%</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3 mt-6">
                      <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded font-medium">
                        설정 저장
                      </button>
                      <button className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded font-medium">
                        전략 삭제
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="bg-white dark:bg-dark-700 border border-light-300 dark:border-dark-600 rounded-lg p-6 text-center">
              <p className="text-gray-600 dark:text-gray-400">전략을 선택하여 상세 정보를 확인하세요</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Strategies; 